/**
 * @group Services
 * 
 * @summary
 * <blockquote>HTTP service implementation for ShenAI Token API</blockquote>
 * 
 * Provides base URL and headers configuration for ShenAI API requests.
 * Used by the backend to generate short-lived tokens for mobile SDK.
 * 
 * @example
 * ```typescript
 * import { ShenAIHttpService } from '@shared/httpService';
 * import { FetchApiClient } from '@shared/httpService';
 * 
 * const service = new ShenAIHttpService(clientSecret);
 * const apiClient = new FetchApiClient({
 *   service: { isLocal: false, service: service }
 * });
 * 
 * const result = await apiClient.post('/v1/token', { expires_in: 3600 });
 * ```
 */

import { ok, err, Result } from '@shared/utils';
import type { AppError } from '@shared/utils';
import type { IHttpService } from '../interfaces/IHttpService';

/** ShenAI API base URL */
const SHENAI_API_BASE_URL = 'https://api.shen.ai';

/**
 * @internal
 */
export class ShenAIHttpService implements IHttpService {
  private readonly clientSecret: string;

  constructor(clientSecret: string) {
    this.clientSecret = clientSecret;
  }

  getBaseUrl(): Result<string, AppError> {
    return ok(SHENAI_API_BASE_URL);
  }

  getHeaders(): Result<Record<string, string>, AppError> {
    if (!this.clientSecret) {
      return err({
        errorCode: 'MISSING_CLIENT_SECRET',
        errorMessage: 'ShenAI client secret is required for API requests'
      });
    }

    return ok({
      'Authorization': `Bearer ${this.clientSecret}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
  }

  getName(): string {
    return 'shenai';
  }
}

